import type {
  ChoiceOption,
  ContentStep,
  QuestionConfig,
  QuestionStep,
  QuestionType,
  SurveyStep,
} from '../../types/survey';

// Phase 6B B3 — client-side step factories. The client owns step-id generation
// (crypto.randomUUID); the server only mints the auto-name step. `name` is never
// created here (it's auto-inserted by normalizeSurveySteps); content_page has
// its own factory since it's a ContentStep, not a QuestionStep.

// Question types offered in the "+ Add question" menu (§3.2) — excludes `name`
// (system-managed) and content_page (a ContentStep, separate menu item).
export type AddableQuestionType = Exclude<QuestionType, 'name'>;

export const ADD_QUESTION_TYPES: { type: AddableQuestionType; label: string }[] = [
  { type: 'yes_no', label: 'Yes / No' },
  { type: 'multiple_choice_single', label: 'Multiple choice (pick one)' },
  { type: 'multiple_choice_multi', label: 'Multiple choice (pick multiple)' },
  { type: 'short_text', label: 'Short text' },
  { type: 'long_text', label: 'Long text' },
  { type: 'rating_scale', label: 'Rating scale (1–N)' },
  { type: 'date', label: 'Date' },
  { type: 'number', label: 'Number' },
];

function option(label: string): ChoiceOption {
  return { id: crypto.randomUUID(), label };
}

function defaultConfig(type: AddableQuestionType): QuestionConfig {
  switch (type) {
    case 'yes_no':
      return { type: 'yes_no' };
    case 'multiple_choice_single':
      return { type: 'multiple_choice_single', options: [option('Option 1'), option('Option 2')] };
    case 'multiple_choice_multi':
      return { type: 'multiple_choice_multi', options: [option('Option 1'), option('Option 2')] };
    case 'short_text':
      return { type: 'short_text' };
    case 'long_text':
      return { type: 'long_text' };
    case 'rating_scale':
      return { type: 'rating_scale', min: 1, max: 10 };
    case 'date':
      return { type: 'date' };
    case 'number':
      return { type: 'number' };
  }
}

export function makeQuestionStep(type: AddableQuestionType): QuestionStep {
  return {
    kind: 'question',
    id: crypto.randomUUID(),
    type,
    required: false,
    prompt: '',
    description: null,
    config: defaultConfig(type),
    branching: [],
    isAutoInserted: false,
  };
}

export function makeContentStep(): ContentStep {
  return {
    kind: 'content',
    id: crypto.randomUUID(),
    type: 'content_page',
    title: '',
    bodyMarkdown: '',
    imageUrl: null,
    ctaLabel: 'Next',
  };
}

// Clone a step into a fresh copy: new step id + new branching-rule ids (so React
// keys + future edits don't collide). Option ids are kept — they're scoped to
// the step and the clone's own branching conditions reference them. Branching
// TARGETS (other steps) are kept as-is; a dangling target after duplication is a
// non-blocking validateSurveyFlow warning the editor surfaces (B5).
export function cloneStep(step: SurveyStep): SurveyStep {
  if (step.kind === 'content') {
    return { ...step, id: crypto.randomUUID() };
  }
  return {
    ...step,
    id: crypto.randomUUID(),
    branching: step.branching.map((r) => ({ ...r, id: crypto.randomUUID() })),
  };
}
